import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    useGetVariantDetailsQuery,
    useGetBrandsQuery,
    useGetCategoriesQuery,
    useGetModelsQuery
} from "../../../service/features/api";
import { CircularProgress } from "@mui/material";
import { getVehicleTypeLabel } from "../../../utils/vehicle";
import CommonBreadcrumb from "../../../components/CommonBreadcrumb";
import moment from "moment";
import { toast } from "react-toastify";

const VariantDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const variantId = location.state?.id;

    const { data: variantData, isLoading } = useGetVariantDetailsQuery(variantId, {
        skip: !variantId,
    });

    const variant = variantData?.data;

    // Additional queries to resolving names if detail is unpopulated
    const { data: brandsData } = useGetBrandsQuery({ page: 1, limit: 100 });
    const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100 });

    // We can only fetch models if we have a brandId. 
    // Try to get from variant object or raw brandId field.
    const brandId = variant?.brand?._id || variant?.brandId;

    const { data: modelsData } = useGetModelsQuery(
        { page: 1, limit: 100, brandId: brandId },
        { skip: !brandId }
    );

    useEffect(() => {
        if (!variantId) {
            toast.error("No variant selected");
            navigate("/manage-variant");
        }
    }, [variantId, navigate]);

    const displayImage = useMemo(() => {
        if (!variant) return "/static/images/placeholder.png";
        if (variant.images && variant.images.length > 0) return variant.images[0];
        return variant.image || "/static/images/placeholder.png";
    }, [variant]);

    const brandName = useMemo(() => {
        if (!variant) return "--";
        // Priority 1: Check brandId object
        if (variant.brandId && typeof variant.brandId === 'object' && variant.brandId.name) {
            return variant.brandId.name;
        }
        // Priority 2: Check brand object
        if (variant.brand?.name) return variant.brand.name;

        // Priority 3: Lookup by ID in brandsData
        // Only if brandId is a string ID
        const bId = (typeof variant.brandId === 'string') ? variant.brandId : variant.brand?._id;
        if (bId && brandsData?.data?.data) {
            const found = brandsData.data.data.find((b: any) => b._id === bId);
            return found ? found.name : "--";
        }
        return "--";
    }, [variant, brandsData]);

    const modelName = useMemo(() => {
        if (!variant) return "--";
        // Priority 1: Check modelId object
        if (variant.modelId && typeof variant.modelId === 'object' && variant.modelId.name) {
            return variant.modelId.name;
        }
        // Priority 2: Check model object
        if (variant.model?.name) return variant.model.name;

        // Priority 3: Lookup by ID in modelsData
        const mId = (typeof variant.modelId === 'string') ? variant.modelId : variant.model?._id;
        if (mId && modelsData?.data?.data) {
            const found = modelsData.data.data.find((m: any) => m._id === mId);
            return found ? found.name : "--";
        }
        return "--";
    }, [variant, modelsData]);

    const categoryName = useMemo(() => {
        if (!variant) return "--";

        // Priority 1: Check categoryId object (single object as per response)
        if (variant.categoryId && !Array.isArray(variant.categoryId) && typeof variant.categoryId === 'object' && (variant.categoryId as any).name) {
            return (variant.categoryId as any).name;
        }

        // Priority 2: Check category (array or object)
        if (variant.category) {
            if (Array.isArray(variant.category)) {
                return variant.category.map((c: any) => c.name).join(", ");
            } else if (variant.category.name) {
                return variant.category.name;
            }
        }

        // Priority 3: Lookup by ID 
        let cIds: string[] = [];
        if (Array.isArray(variant.categoryId)) {
            // If it's array of strings
            cIds = variant.categoryId.filter((id: any) => typeof id === 'string');
        } else if (typeof variant.categoryId === 'string') {
            cIds = [variant.categoryId];
        }

        if (cIds.length > 0 && categoriesData?.data?.data) {
            const names = cIds.map((id: string) => {
                const found = categoriesData.data.data.find((c: any) => c._id === id);
                return found ? found.name : null;
            }).filter(Boolean);
            if (names.length > 0) return names.join(", ");
        }

        return "--";
    }, [variant, categoriesData]);


    if (isLoading || !variant) {
        return (
            <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
                <CircularProgress />
            </div>
        );
    }

    return (
        <>
            <CommonBreadcrumb
                title="Variant Detail"
                breadcrumbItems={[
                    { label: "Dashboard", onClick: () => navigate("/dashboard") },
                    { label: "Manage Variant", onClick: () => navigate("/manage-variant") },
                    { label: "Variant Detail" },
                ]}
            />

            <div className="cards view_page">
                <div className="gap_p">
                    <div className="vew_img w_33">
                        <h6>Variant Image</h6>
                        <figure>
                            <img
                                src={displayImage}
                                alt={variant.name}
                                onError={(e: any) => { e.target.src = "/static/images/placeholder.png" }}
                            />
                        </figure>
                    </div>
                    <div className="w_33">
                        <h6>Variant Name</h6>
                        <p>{variant.name}</p>
                    </div>
                    <div className="w_33">
                        <h6>Vehicle Type</h6>
                        <p>{getVehicleTypeLabel(variant.type)}</p>
                    </div>
                    <div className="w_33">
                        <h6>Brand</h6>
                        <p>{brandName}</p>
                    </div>
                    <div className="w_33">
                        <h6>Model</h6>
                        <p>{modelName}</p>
                    </div>
                    <div className="w_33">
                        <h6>Category</h6>
                        <p>{categoryName}</p>
                    </div>
                    <div className="w_33">
                        <h6>Created Date</h6>
                        <p>{moment(variant.createdAt).format("DD MMM YYYY")}</p>
                    </div>

                    <div className="w_33">
                        <h6>Popular</h6>
                        <p>{variant.isPopular ? "Yes" : "No"}</p>
                    </div>
                    <div className="w_33">
                        <h6>Status</h6>
                        <span className={`status_btn dark ${variant.isActive ? "c_success" : "c_danger"}`}>
                            {variant.isActive ? "Active" : "Inactive"}
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default VariantDetail;
